import React, { PureComponent } from 'react';
import { arrayOf, func, shape, string, any, object, bool } from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ListItem from '@material-ui/core/ListItem';
import styled, { css } from 'styled-components';
import { colors } from '../../constants/colors';
import { fontSize } from '../../constants/text';
import { biggerThan, smallerThanOrEqualTo } from '../../core/util/styledComponents';
import AutocompleteIcon from './AutocompleteIcon';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { isEmpty } from '../../core/util/empty';

const TIMEOUT = 100;

const Root = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  & .item-enter {
    opacity: 0;
    transform: translateY(-5px);
  }

  & .item-enter-active {
    opacity: 1;
    transform: translateY(0);
  }

  & .item-exit {
    opacity: 1;
    transform: translateY(0);
  }

  & .item-exit-active {
    opacity: 0;
    transform: translateY(-5px);
  }
`;

const Icon = styled(AutocompleteIcon)`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  ${biggerThan.sm`
    margin-right: 10px;
  `}
`;

const Suggestion = styled.div`
  ${smallerThanOrEqualTo.sm`
    margin-left: 10px;
  `}
`;

const Item = styled(ListItem)`
  box-sizing: border-box;
  padding: 10px;
  display: flex;
  ${props => css`
    min-height: ${props.sc.narrow ? '20' : '50'}px;
  `};
  border-top: 1px solid ${colors.borderLine};
  background-color: ${colors.white};
  ${props =>
    props.sc.arrow &&
    css`
      background-repeat: no-repeat;
      background-image: url(/images/icons/arrow/grey_forward.svg);
      background-size: 8px 13px;
      background-position: right 10px center;
    `};

  transition: background-color 100ms ease;
  ${props =>
    props.sc.hoverEffect &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${colors.greyHounds};
      }
    `}

  &:last-child {
    border-bottom: 1px solid ${colors.borderLine};
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const Primary = styled.div`
  color: ${colors.myRightFoot};
  font-size: ${fontSize.default};
  line-height: 20px;

  & em {
    color: ${colors.crunchBerry};
    font-style: normal;
  }
`;

const Secondary = styled.div`
  color: ${colors.lightText};
  font-size: ${fontSize.extraSmall};
  line-height: ${fontSize.mediumSmall};
`;

const StyledCircularProgress = styled(CircularProgress)`
  position: absolute;
  right: 10%;
  top: 20px;
`;

export default class AutocompleteResults extends PureComponent {
  static propTypes = {
    className: string,
    position: object,
    results: arrayOf(
      shape({
        primary: string,
        secondary: string,
        key: string.isRequired,
        icon: any,
        onClick: func,
        narrow: bool,
        showPositionLoader: bool,
      })
    ),
  };

  shouldShowArrow = result => undefined;

  render() {
    const { className, results, position } = this.props;
    const suggestions = results.length ? results : [];
    return (
      <Root className={className}>
        <TransitionGroup component={null}>
          {suggestions.map(result => (
            <CSSTransition classNames="item" timeout={TIMEOUT} key={result.key}>
              <Item
                sc={{
                  arrow: this.shouldShowArrow(result),
                  narrow: result.narrow,
                  hoverEffect: !!result.onClick,
                }}
                component="li"
                onClick={result.onClick}
              >
                <Icon suggestion={result} />
                <Suggestion>
                  <Primary dangerouslySetInnerHTML={{ __html: result.primary }} />
                  <Secondary>{result.secondary}</Secondary>
                </Suggestion>
                {result.showPositionLoader && position.gettingPosition && (
                  <StyledCircularProgress size={13} />
                )}
              </Item>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </Root>
    );
  }
}
